import React from "react";

const TRACKING_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]);

const LINK_CLASS_NAME = "break-words text-current underline underline-offset-2 [overflow-wrap:anywhere]";
const TEXT_CLASS_NAME = "min-w-0 break-words [overflow-wrap:anywhere]";

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function safeDecodeQueryKey(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
}

export function removeTrackingParamsFromUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || !isHttpUrl(trimmed)) return url;

  const hashIndex = trimmed.indexOf("#");
  const beforeHash = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
  const hash = hashIndex >= 0 ? trimmed.slice(hashIndex) : "";
  const queryIndex = beforeHash.indexOf("?");

  if (queryIndex < 0) return trimmed;

  const base = beforeHash.slice(0, queryIndex);
  const query = beforeHash.slice(queryIndex + 1);
  const parts = query.split("&");
  const kept = parts.filter((part) => {
    const rawKey = part.split("=", 1)[0] ?? "";
    return !TRACKING_QUERY_PARAMS.has(safeDecodeQueryKey(rawKey).toLowerCase());
  });

  if (kept.length === parts.length) return trimmed;
  return `${base}${kept.length > 0 ? `?${kept.join("&")}` : ""}${hash}`;
}

function getSafeHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    const normalized = removeTrackingParamsFromUrl(trimmed);
    return isHttpUrl(normalized) ? normalized : null;
  }
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^tel:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;

  return null;
}

function normalizeLinkLabel(label: string): string {
  const trimmed = label.trim();
  if (!isHttpUrl(trimmed)) return label;
  return removeTrackingParamsFromUrl(trimmed);
}

function hasMoreClosingThanOpening(value: string, opening: string, closing: string): boolean {
  let openingCount = 0;
  let closingCount = 0;

  for (const char of value) {
    if (char === opening) openingCount += 1;
    if (char === closing) closingCount += 1;
  }

  return closingCount > openingCount;
}

function shouldTrimTrailingUrlChar(url: string, char: string): boolean {
  if (char === ")" || char === "]") {
    return hasMoreClosingThanOpening(url, char === ")" ? "(" : "[", char);
  }
  if (char === "）") return hasMoreClosingThanOpening(url, "（", "）");
  if (char === "】") return hasMoreClosingThanOpening(url, "【", "】");
  if (char === "」") return hasMoreClosingThanOpening(url, "「", "」");

  return char === "。" || char === "、" || char === "." || char === ",";
}

function trimTrailingUrlPunctuation(rawUrl: string): { url: string; trailing: string } {
  let url = rawUrl;
  let trailing = "";

  while (url.length > 0) {
    const char = url.slice(-1);
    if (!shouldTrimTrailingUrlChar(url, char)) break;
    url = url.slice(0, -1);
    trailing = `${char}${trailing}`;
  }

  return { url, trailing };
}

function createLink(key: string, href: string, label: string) {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={LINK_CLASS_NAME}
    >
      {label}
    </a>
  );
}

function pushPlainTextWithLinks(parts: React.ReactNode[], text: string, keyPrefix: string) {
  const urlPattern = /https?:\/\/[^\s<]+/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const { url, trailing } = trimTrailingUrlPunctuation(match[0]);
    const href = getSafeHref(url);

    if (href) {
      parts.push(createLink(`${keyPrefix}-url-${match.index}-${href}`, href, removeTrackingParamsFromUrl(url)));
    } else {
      parts.push(url);
    }
    if (trailing) {
      parts.push(trailing);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
}

function splitTextWithLinks(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const markdownLinkPattern = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = markdownLinkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushPlainTextWithLinks(parts, text.slice(lastIndex, match.index), `${keyPrefix}-plain-${match.index}`);
    }

    const href = getSafeHref(match[2]);
    if (href) {
      parts.push(createLink(`${keyPrefix}-md-${match.index}-${href}`, href, normalizeLinkLabel(match[1])));
    } else {
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushPlainTextWithLinks(parts, text.slice(lastIndex), `${keyPrefix}-tail-${lastIndex}`);
  }

  return parts;
}

function stripHtmlLike(input: string): string {
  return input.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "").replace(/<\/?[^>]+>/g, "");
}

function renderRichTextFromHtmlLike(input: string): React.ReactNode {
  if (typeof document === "undefined") {
    const withBreaks = input.replace(/<br\s*\/?>/gi, "\n");
    return splitTextWithLinks(stripHtmlLike(withBreaks), "ssr");
  }

  const template = document.createElement("template");
  template.innerHTML = input;

  let key = 0;
  const out: React.ReactNode[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? "";
      if (text) out.push(...splitTextWithLinks(text, `text-${key++}`));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === "br") {
      out.push(<br key={`br-${key++}`} />);
      return;
    }

    if (tag === "a") {
      const href = getSafeHref(el.getAttribute("href") || "");
      const label = (el.textContent || "").trim() || href || "";

      if (href) {
        out.push(createLink(`a-${key++}-${href}`, href, normalizeLinkLabel(label)));
      } else if (label) {
        out.push(label);
      }
      return;
    }

    if (tag === "script" || tag === "style") {
      return;
    }

    Array.from(node.childNodes).forEach(walk);
  };

  Array.from(template.content.childNodes).forEach(walk);

  if (out.length === 0) return splitTextWithLinks(stripHtmlLike(input), "fallback");
  return out;
}

export function ChatRichText({ text }: { text: string }) {
  return <span className={TEXT_CLASS_NAME}>{renderRichTextFromHtmlLike(text)}</span>;
}
